import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  MessageSquare,
  Mic,
  Volume2,
  Search,
  MemoryStick,
  Info,
  Sparkles,
  Check,
  Layers,
} from 'lucide-react';
import { NeMoPretrainedModel, NeMoTaskType, NeMoTrainingMethod } from '@/mock/gpu-instance';

interface NeMoModelSelectorProps {
  models: NeMoPretrainedModel[];
  selectedModelId?: string;
  onSelectModel: (model: NeMoPretrainedModel) => void;
  onViewModelCard?: (model: NeMoPretrainedModel) => void;
  filterTask?: NeMoTaskType;
  filterMethod?: NeMoTrainingMethod;
}

const getTaskIcon = (task: NeMoTaskType) => {
  switch (task) {
    case 'llm':
      return <MessageSquare className="h-4 w-4 text-green-500" />;
    case 'asr':
      return <Mic className="h-4 w-4 text-blue-500" />;
    case 'tts':
      return <Volume2 className="h-4 w-4 text-purple-500" />;
  }
};

const getTaskLabel = (task: NeMoTaskType) => {
  switch (task) {
    case 'llm':
      return 'LLM';
    case 'asr':
      return 'ASR';
    case 'tts':
      return 'TTS';
  }
};

const NeMoModelSelector = ({
  models,
  selectedModelId,
  onSelectModel,
  onViewModelCard,
  filterTask,
  filterMethod,
}: NeMoModelSelectorProps) => {
  const [search, setSearch] = useState('');
  const [taskFilter, setTaskFilter] = useState<string>(filterTask || 'all');
  const [familyFilter, setFamilyFilter] = useState<string>('all');

  // Get unique families
  const families = [...new Set(models.map((m) => m.family))];

  // Filter models
  const filteredModels = models.filter((model) => {
    const matchesSearch =
      model.displayName.toLowerCase().includes(search.toLowerCase()) ||
      model.description.toLowerCase().includes(search.toLowerCase()) ||
      model.family.toLowerCase().includes(search.toLowerCase());
    const matchesTask = taskFilter === 'all' || model.task === taskFilter;
    const matchesFamily = familyFilter === 'all' || model.family === familyFilter;
    const matchesMethod = !filterMethod || model.supportedMethods.includes(filterMethod);
    return matchesSearch && matchesTask && matchesFamily && matchesMethod;
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search models..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        {!filterTask && (
          <Select value={taskFilter} onValueChange={setTaskFilter}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Task" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tasks</SelectItem>
              <SelectItem value="llm">LLM</SelectItem>
              <SelectItem value="asr">ASR</SelectItem>
              <SelectItem value="tts">TTS</SelectItem>
            </SelectContent>
          </Select>
        )}
        <Select value={familyFilter} onValueChange={setFamilyFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Family" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Families</SelectItem>
            {families.map((family) => (
              <SelectItem key={family} value={family}>
                {family}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Models Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[400px] overflow-y-auto pr-2">
        {filteredModels.map((model) => {
          const isSelected = model.id === selectedModelId;
          return (
            <Card
              key={model.id}
              className={`cursor-pointer transition-all hover:border-primary/50 ${
                isSelected ? 'border-primary ring-1 ring-primary' : ''
              }`}
              onClick={() => onViewModelCard?.(model)}
            >
              <CardContent className="pt-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getTaskIcon(model.task)}
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {model.displayName}
                        {isSelected && (
                          <Check className="h-4 w-4 text-primary" />
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{model.family}</div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewModelCard?.(model);
                    }}
                  >
                    <Info className="h-4 w-4" />
                  </Button>
                </div>

                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  {model.description}
                </p>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-2 mb-3">
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <div className="text-xs text-muted-foreground">Params</div>
                    <div className="font-mono text-sm font-medium">{model.parameters}</div>
                  </div>
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                      <MemoryStick className="h-3 w-3" />
                      Full
                    </div>
                    <div className="font-mono text-sm font-medium">{model.vramRequired}GB</div>
                  </div>
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      LoRA
                    </div>
                    <div className="font-mono text-sm font-medium">{model.vramLoRA}GB</div>
                  </div>
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                      <Layers className="h-3 w-3" />
                      Classes
                    </div>
                    <div className="font-mono text-sm font-medium">
                      {model.outputClasses >= 1000
                        ? `${(model.outputClasses / 1000).toFixed(0)}K`
                        : model.outputClasses}
                    </div>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1">
                  <Badge variant="secondary" className="text-xs">
                    {getTaskLabel(model.task)}
                  </Badge>
                  {model.contextLength && (
                    <Badge variant="outline" className="text-xs">
                      {(model.contextLength / 1000).toFixed(0)}K ctx
                    </Badge>
                  )}
                  {model.languages && (
                    <Badge variant="outline" className="text-xs">
                      {model.languages.join(', ').toUpperCase()}
                    </Badge>
                  )}
                  {model.supportedMethods.includes('lora') && (
                    <Badge className="bg-green-500/10 text-green-600 border-green-500/30 text-xs">
                      LoRA
                    </Badge>
                  )}
                  {model.supportedMethods.includes('qlora') && (
                    <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30 text-xs">
                      QLoRA
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* No Results */}
      {filteredModels.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Search className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>No models found matching your criteria</p>
        </div>
      )}

      {/* Count */}
      <div className="text-sm text-muted-foreground text-center">
        Showing {filteredModels.length} of {models.length} models
      </div>
    </div>
  );
};

export default NeMoModelSelector;
